import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '../lib/supabase';
import { getMyCountry, filterByGeoRestriction } from '../lib/geoRestrict';
import ConnectionWindow from './ConnectionWindow';
import { genderColor, getAvatarHTML } from '../lib/avatars';
import { BlockModal, ReportModal } from './BlockReport';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const ONLINE_THRESHOLD_MINUTES = 60; // 60 min = offline // Online si actif dans les 10 dernières minutes
const LOCATION_UPDATE_INTERVAL = 4 * 60 * 60 * 1000; // 4h

function randomize(lat, lng, km=1) {
  const r=km/111, a=Math.random()*2*Math.PI, d=Math.random()*r;
  return { lat: lat+d*Math.cos(a), lng: lng+d*Math.sin(a) };
}
function distKm(a,b,c,d) {
  const R=6371,dL=(c-a)*Math.PI/180,dN=(d-b)*Math.PI/180;
  const x=Math.sin(dL/2)**2+Math.cos(a*Math.PI/180)*Math.cos(c*Math.PI/180)*Math.sin(dN/2)**2;
  return Math.round(R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x))*10)/10;
}
function isOnline(lastSeen) {
  if (!lastSeen) return false;
  const diff = (Date.now() - new Date(lastSeen).getTime()) / 1000 / 60;
  return diff < ONLINE_THRESHOLD_MINUTES;
}
function isCompatible(myG,myO,theirG,theirO) {
  if(!myO||!theirO) return true;
  if(['Pansexual','Bisexual'].includes(myO)||['Pansexual','Bisexual'].includes(theirO)) return true;
  if(['Non-binary','Other'].includes(myG)||['Non-binary','Other'].includes(theirG)) return true;
  if(myO==='Straight'&&myG==='Man') return theirG==='Woman';
  if(myO==='Straight'&&myG==='Woman') return theirG==='Man';
  if(myO==='Gay'&&myG==='Man') return theirG==='Man';
  if(myO==='Lesbian'||(myO==='Gay'&&myG==='Woman')) return theirG==='Woman';
  return true;
}

const FilterIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><line x1="1" y1="3" x2="12" y2="3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><line x1="3" y1="6.5" x2="10" y2="6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><line x1="5" y1="10" x2="8" y2="10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const EyeIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 6.5C1 6.5 3.5 2.5 6.5 2.5C9.5 2.5 12 6.5 12 6.5C12 6.5 9.5 10.5 6.5 10.5C3.5 10.5 1 6.5 1 6.5Z" stroke="currentColor" strokeWidth="1.3"/><circle cx="6.5" cy="6.5" r="1.8" stroke="currentColor" strokeWidth="1.3"/></svg>;
const EyeOffIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 1L12 12M3 4C2 5 1 6 1 6.5C1 7 3.5 10 6.5 10C7.5 10 8.5 9.7 9.2 9.2M5 3C5.5 2.7 6 2.5 6.5 2.5C9.5 2.5 12 5.5 12 6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const CloseIcon = () => <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
const MsgIcon = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1H13V10H8L5 13V10H1V1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
const BlockIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.3"/><line x1="2" y1="2" x2="11" y2="11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const FlagIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 1V12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M2 1L9 3L2 6" fill="currentColor" opacity="0.7"/></svg>;
const ChatIcon = () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1 1H12V9H7L4.5 11.5V9H1V1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;

const intentionColors = {'Relationship':'#00d68f','Something casual':'#ff9500','Friendship':'#0084ff','Not sure yet':'#888'};
const REPORT_REASONS = ['Fake profile','Harassment','Spam','Inappropriate content','Other'];

export default function Map({ session, onOpenChat, onUsersUpdate, unreadCounts={}, onBlock, blockedIds=new Set() }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef({});
  const lastLocationUpdate = useRef(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [conversationIds, setConversationIds] = useState(new Set()); // users avec qui on a parlé
  const [showFilters, setShowFilters] = useState(false);
  const [visible, setVisible] = useState(true);
  const [myCountry, setMyCountry] = useState('UNKNOWN');
  const [filters, setFilters] = useState({intention:'All', maxAge:60, minAge:18, onlineOnly:false});
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [blockDone, setBlockDone] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeWindow, setActiveWindow] = useState(null);
  const [sentWindow, setSentWindow] = useState(null);

  const myId = session?.user?.id;

  useEffect(() => {
    getMyCountry().then(country => setMyCountry(country));
  }, []);
  const myGender = session?.user?.user_metadata?.gender||'';
  const myOrientation = session?.user?.user_metadata?.orientation||'';

  // Charger les IDs des users avec qui on a déjà eu une conversation
  useEffect(() => {
    if (!myId) return;
    const load = async () => {
      const { data } = await supabase.from('messages').select('sender_id, receiver_id')
        .or(`sender_id.eq.${myId},receiver_id.eq.${myId}`);
      if (!data) return;
      const ids = new Set(data.map(m => m.sender_id === myId ? m.receiver_id : m.sender_id));
      setConversationIds(ids);
    };
    load();
  }, [myId]);

  // Block
  const blockUser = async (userId) => {
    const _b = await supabase.from('blocks').insert({ blocker_id: myId, blocked_id: userId });
    if (onBlock) onBlock(userId);
    setNearbyUsers(prev => prev.filter(u => u.id !== userId));
    if (markers.current[userId]) { markers.current[userId].remove(); delete markers.current[userId]; }
    setSelectedUser(null); setTargetUser(null);
  };

  const confirmBlock = async () => {
    if (!targetUser || !myId) return;
    setActionLoading(true);
    try {
      // Delete first to avoid unique constraint
      await supabase.from('blocks').delete().eq('blocker_id', myId).eq('blocked_id', targetUser.id);
      const { error } = await supabase.from('blocks').insert({ blocker_id: myId, blocked_id: targetUser.id });
      if (error) throw error;
      // Remove from UI immediately
      const uid = targetUser.id;
      if (markers.current[uid]) { markers.current[uid].remove(); delete markers.current[uid]; }
      setNearbyUsers(prev => prev.filter(u => u.id !== uid));
      if (onBlock) onBlock(uid);
      setBlockDone(true);
      setTimeout(() => {
        setBlockDone(false);
        setShowBlockConfirm(false);
        setTargetUser(null);
        setSelectedUser(null);
      }, 1200);
    } catch(e) {
      console.error('Block failed:', e);
      alert('Block failed: ' + (e.message || 'Unknown error'));
    }
    setActionLoading(false);
  };

  const submitReport = async () => {
    if (!targetUser || !myId || !reportReason) return;
    setActionLoading(true);
    await supabase.from('reports').insert({ reporter_id: myId, reported_id: targetUser.id, reason: reportReason, message: reportMessage || null });
    setActionLoading(false);
    setReportDone(true);
    setTimeout(() => { setReportDone(false); setShowReportModal(false); setTargetUser(null); setReportReason(''); setReportMessage(''); setSelectedUser(null); }, 1800);
  };

  useEffect(() => {
    if (!navigator.geolocation || !myId) return;
    navigator.geolocation.getCurrentPosition(async pos => {
      const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setMyLoc(loc);
      if (visible) {
        await supabase.from('user_locations').upsert({
          id: myId,
          lat: loc.lat,
          lng: loc.lng,
          updated_at: new Date().toISOString()
        });
      }
    }, null, { enableHighAccuracy: true });
  }, [myId]);

  // Charger users
  useEffect(() => {
    if (!myLoc) return;
    const load = async () => {
      const { data: locs } = await supabase.from('user_locations').select('*').neq('id', myId||'');
      if (!locs?.length) return;
      const { data: profiles } = await supabase.from('profiles').select('*').in('id', locs.map(l=>l.id)).eq('visible', true);
      if (!profiles) return;
      const users = profiles
        .filter(p => !blockedIds.has(p.id))
        .map(p => {
          const loc = locs.find(l=>l.id===p.id);
          return { ...p, lat:loc?.lat, lng:loc?.lng, dist:loc?distKm(myLoc.lat,myLoc.lng,loc.lat,loc.lng):999, lastSeen:loc?.last_seen, online:isOnline(loc?.last_seen) };
        })
        .filter(u => isCompatible(myGender, myOrientation, u.gender, u.orientation))
        .filter(u => {
          if (!u.lastSeen) return false;
          const diffMin = (Date.now() - new Date(u.lastSeen).getTime()) / 60000;
          return diffMin < 60;
        })
        .sort((a,b) => {
          // Online d'abord, puis par distance
          if (a.online && !b.online) return -1;
          if (!a.online && b.online) return 1;
          return a.dist - b.dist;
        });
      const geoFiltered = filterByGeoRestriction(users, myCountry);
      setNearbyUsers(geoFiltered);
      if (onUsersUpdate) onUsersUpdate(users);
    };
    load();
    const ch = supabase.channel('locs').on('postgres_changes',{event:'*',schema:'public',table:'user_locations'},load).subscribe();
    return () => supabase.removeChannel(ch);
  }, [myLoc, myId, myGender, myOrientation, blockedIds]);

  // Init map
  useEffect(() => {
    if (map.current || !MAPBOX_TOKEN || !myLoc) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    map.current = new mapboxgl.Map({ container:mapContainer.current, style:'mapbox://styles/mapbox/dark-v11', center:[myLoc.lng,myLoc.lat], zoom:13 });
    map.current.addControl(new mapboxgl.NavigationControl(),'top-right');
    const me = randomize(myLoc.lat,myLoc.lng,0.3);
    const el = document.createElement('div');
    el.innerHTML = '<div style="width:42px;height:42px;border-radius:50%;background:white;border:2px solid #080808;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#080808;box-shadow:0 0 0 4px rgba(255,255,255,0.1);">ME</div>';
    new mapboxgl.Marker({element:el}).setLngLat([me.lng,me.lat]).addTo(map.current);
  }, [myLoc]);

  // Markers — online/offline + déjà parlé
  useEffect(() => {
    if (!map.current) return;
    Object.values(markers.current).forEach(m=>m.remove());
    markers.current = {};
    nearbyUsers.filter(u=>!blockedIds.has(u.id)).forEach(u => {
      const pos = randomize(u.lat, u.lng, 2);
      const color = genderColor(u.gender);
      const isConvo = conversationIds.has(u.id);
      const online = u.online;
      const el = document.createElement('div');
      // Avatar avec indicateur online/offline et badge conversation
      el.innerHTML = `<div style="position:relative;width:46px;height:46px;">
        <div style="width:44px;height:44px;border-radius:50%;background:rgba(8,8,8,0.9);border:2px solid ${online?color:'rgba(255,255,255,0.2)'};display:flex;align-items:center;justify-content:center;cursor:pointer;opacity:${online?1:0.5};transition:transform 0.2s;" onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
          ${getAvatarHTML(u.gender, u.id, 40).match(/<svg[^>]*>[\s\S]*<\/svg>/)?.[0]||''}
        </div>
        <div style="position:absolute;bottom:1px;right:1px;width:11px;height:11px;border-radius:50%;background:${online?'#00d68f':'rgba(255,255,255,0.2)'};border:2px solid #080808;"></div>
        ${isConvo?`<div style="position:absolute;top:-2px;left:-2px;width:14px;height:14px;border-radius:50%;background:#0084ff;border:2px solid #080808;display:flex;align-items:center;justify-content:center;"><svg width="7" height="7" viewBox="0 0 7 7" fill="none"><path d="M0.5 0.5H6.5V5H4L2.5 6.5V5H0.5V0.5Z" fill="white"/></svg></div>`:''}
      </div>`;
      const marker = new mapboxgl.Marker({element:el}).setLngLat([pos.lng,pos.lat]).addTo(map.current);
      el.addEventListener('click', ()=>setSelectedUser(u));
      markers.current[u.id] = marker;
    });
  }, [nearbyUsers, blockedIds, conversationIds]);

  const filtered = nearbyUsers.filter(u =>
    !blockedIds.has(u.id) &&
    (filters.intention==='All'||u.intention===filters.intention) &&
    (!u.age||(u.age>=filters.minAge&&u.age<=filters.maxAge)) &&
    (!filters.onlineOnly||u.online)
  );

  const onlineCount = filtered.filter(u=>u.online).length;
  const chip = (active,label,onClick) => <button onClick={onClick} style={{padding:'6px 10px',background:active?'white':'rgba(255,255,255,0.05)',border:'none',borderRadius:'100px',color:active?'#080808':'rgba(255,255,255,0.4)',fontSize:'0.75rem',fontWeight:active?600:400,cursor:'pointer',whiteSpace:'nowrap'}}>{label}</button>;

  if (!myLoc) return (
    <div style={{height:'calc(100vh - 60px)',marginTop:'60px',display:'flex',alignItems:'center',justifyContent:'center',background:'#080808',flexDirection:'column',gap:'12px'}}>
      <div style={{width:'28px',height:'28px',border:'2px solid rgba(255,255,255,0.08)',borderTop:'2px solid white',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
      <p style={{color:'rgba(255,255,255,0.25)',fontSize:'0.82rem'}}>Getting your location...</p>
    </div>
  );

  const lastSeenText = (lastSeen) => {
    if (!lastSeen) return 'Offline';
    const diff = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 1000 / 60);
    if (diff < 10) return 'Online now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
    return `${Math.floor(diff/1440)}d ago`;
  };

  return (
    <div style={{position:'relative',height:'calc(100vh - 60px)',marginTop:'60px'}}>
      <div ref={mapContainer} style={{width:'100%',height:'100%'}}/>

      {/* Badge nearby */}
      <div style={{position:'absolute',top:'10px',left:'50%',transform:'translateX(-50%)',background:'rgba(8,8,8,0.9)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'100px',padding:'5px 14px',fontSize:'0.73rem',color:'rgba(255,255,255,0.35)',display:'flex',alignItems:'center',gap:'6px',whiteSpace:'nowrap',zIndex:10}}>
        <div style={{width:5,height:5,borderRadius:'50%',background:'#00d68f'}}/>
        {onlineCount} online · {filtered.length} nearby
      </div>

      {/* Légende */}
      <div style={{position:'absolute',top:'48px',left:'50%',transform:'translateX(-50%)',background:'rgba(8,8,8,0.7)',borderRadius:'100px',padding:'3px 11px',display:'flex',alignItems:'center',gap:'10px',zIndex:10}}>
        <div style={{display:'flex',alignItems:'center',gap:'4px'}}><div style={{width:7,height:7,borderRadius:'50%',background:'#00d68f'}}/><span style={{fontSize:'0.62rem',color:'rgba(255,255,255,0.25)'}}>Online</span></div>
        <div style={{display:'flex',alignItems:'center',gap:'4px'}}><div style={{width:7,height:7,borderRadius:'50%',background:'rgba(255,255,255,0.2)'}}/><span style={{fontSize:'0.62rem',color:'rgba(255,255,255,0.25)'}}>Offline</span></div>
        <div style={{display:'flex',alignItems:'center',gap:'4px'}}><div style={{width:7,height:7,borderRadius:'50%',background:'#0084ff'}}/><span style={{fontSize:'0.62rem',color:'rgba(255,255,255,0.25)'}}>Conversation</span></div>
      </div>

      {/* Toolbar */}
      <div style={{position:'absolute',top:'80px',left:'16px',display:'flex',gap:'6px',zIndex:10}}>
        <button onClick={()=>setShowFilters(!showFilters)} style={{padding:'8px 12px',background:'rgba(8,8,8,0.9)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'100px',color:'rgba(255,255,255,0.6)',fontSize:'0.75rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}><FilterIcon/> Filters</button>
        <button onClick={()=>setVisible(!visible)} style={{padding:'8px 12px',background:visible?'rgba(255,255,255,0.06)':'rgba(8,8,8,0.9)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'100px',color:visible?'white':'rgba(255,255,255,0.35)',fontSize:'0.75rem',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}>
          {visible?<EyeIcon/>:<EyeOffIcon/>} {visible?'Visible':'Hidden'}
        </button>
      </div>

      {/* Filters panel */}
      {/* Locate me */}
      <button onClick={() => { if(map.current && myLoc) map.current.flyTo({center:[myLoc.lng,myLoc.lat],zoom:14,duration:1000}); }}
        style={{position:'absolute',top:'52px',right:'60px',zIndex:10,width:'36px',height:'36px',background:'rgba(8,8,8,0.9)',backdropFilter:'blur(10px)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'50%',cursor:'pointer',color:'white',display:'flex',alignItems:'center',justifyContent:'center'}}
        title="Center on me">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
          <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" opacity="0.4"/>
          <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>

      {showFilters && (
        <div style={{position:'absolute',top:'122px',left:'10px',right:'10px',background:'rgba(8,8,8,0.97)',backdropFilter:'blur(20px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'16px',padding:'16px',zIndex:50}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
            <span style={{fontSize:'0.88rem',fontWeight:600,color:'white'}}>Filters</span>
            <button onClick={()=>setShowFilters(false)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.3)',cursor:'pointer'}}><CloseIcon/></button>
          </div>
          <div style={{marginBottom:'10px'}}>
            <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.25)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'6px'}}>Status</div>
            <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
              {chip(!filters.onlineOnly,'All',()=>setFilters({...filters,onlineOnly:false}))}
              {chip(filters.onlineOnly,'Online only',()=>setFilters({...filters,onlineOnly:true}))}
            </div>
          </div>
          <div style={{marginBottom:'10px'}}>
            <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.25)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'6px'}}>Looking for</div>
            <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
              {['All','Relationship','Something casual','Friendship','Not sure yet'].map(i=>chip(filters.intention===i,i,()=>setFilters({...filters,intention:i})))}
            </div>
          </div>
          <div>
            <div style={{fontSize:'0.68rem',color:'rgba(255,255,255,0.25)',textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:'6px'}}>Age: {filters.minAge} — {filters.maxAge}</div>
            <input type="range" min="18" max="70" value={filters.maxAge} onChange={e=>setFilters({...filters,maxAge:parseInt(e.target.value)})} style={{width:'100%',accentColor:'white'}}/>
          </div>
        </div>
      )}

      {/* Profile card */}
      {selectedUser && !blockedIds.has(selectedUser.id) && !showBlockConfirm && !showReportModal && (
        <div style={{position:'absolute',bottom:'10px',left:'10px',right:'10px',background:'rgba(8,8,8,0.97)',backdropFilter:'blur(20px)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'20px',padding:'18px',zIndex:50,boxShadow:'0 20px 60px rgba(0,0,0,0.8)'}}>
          <div style={{display:'flex',gap:'12px',marginBottom:'10px'}}>
            <div style={{position:'relative',flexShrink:0}}>
              <div style={{width:'48px',height:'48px',borderRadius:'50%',background:'rgba(8,8,8,0.9)',border:`2px solid ${selectedUser.online?genderColor(selectedUser.gender):'rgba(255,255,255,0.15)'}`,display:'flex',alignItems:'center',justifyContent:'center',opacity:selectedUser.online?1:0.6}} dangerouslySetInnerHTML={{__html:getAvatarHTML(selectedUser.gender,selectedUser.id,44).replace(/width:\d+px;height:\d+px/,'width:44px;height:44px')}}/>
              <div style={{position:'absolute',bottom:'0px',right:'0px',width:'12px',height:'12px',borderRadius:'50%',background:selectedUser.online?'#00d68f':'rgba(255,255,255,0.2)',border:'2px solid #080808'}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:'7px'}}>
                <h3 style={{fontSize:'0.95rem',fontWeight:600,color:'white',margin:0}}>{selectedUser.name||'User'}, {selectedUser.age||'?'}</h3>
                {conversationIds.has(selectedUser.id) && (
                  <div style={{display:'flex',alignItems:'center',gap:'3px',background:'rgba(0,132,255,0.12)',padding:'2px 7px',borderRadius:'100px'}}>
                    <ChatIcon/>
                    <span style={{fontSize:'0.65rem',color:'#0084ff'}}>Conversation</span>
                  </div>
                )}
              </div>
              <div style={{color:selectedUser.online?'#00d68f':'rgba(255,255,255,0.3)',fontSize:'0.72rem',marginTop:'2px'}}>
                {lastSeenText(selectedUser.lastSeen)}
              </div>
              {selectedUser.intention && <div style={{display:'inline-block',marginTop:'4px',fontSize:'0.7rem',color:intentionColors[selectedUser.intention]||'#888',background:'rgba(255,255,255,0.04)',padding:'2px 8px',borderRadius:'100px'}}>{selectedUser.intention}</div>}
            </div>
            <div style={{display:'flex',gap:'6px',flexShrink:0}}>
              <button onClick={()=>{setTargetUser(selectedUser);setShowReportModal(true);}} style={{width:'30px',height:'30px',background:'rgba(255,165,0,0.1)',border:'1px solid rgba(255,165,0,0.2)',borderRadius:'50%',cursor:'pointer',color:'rgba(255,165,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center'}}><FlagIcon/></button>
              <button onClick={()=>{setTargetUser(selectedUser);setShowBlockConfirm(true);}} style={{width:'30px',height:'30px',background:'rgba(255,50,50,0.1)',border:'1px solid rgba(255,50,50,0.2)',borderRadius:'50%',cursor:'pointer',color:'rgba(255,100,100,0.85)',display:'flex',alignItems:'center',justifyContent:'center'}}><BlockIcon/></button>
              <button onClick={()=>setSelectedUser(null)} style={{background:'rgba(255,255,255,0.05)',color:'rgba(255,255,255,0.3)',border:'none',borderRadius:'50%',width:'30px',height:'30px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><CloseIcon/></button>
            </div>
          </div>
          {selectedUser.bio && <p style={{color:'rgba(255,255,255,0.4)',fontSize:'0.82rem',marginBottom:'6px',lineHeight:1.5}}>{selectedUser.bio}</p>}
          <p style={{color:'rgba(255,255,255,0.18)',fontSize:'0.72rem',marginBottom:'12px'}}>~{selectedUser.dist} km · position approximate</p>
          <div style={{display:'flex',gap:'8px'}}>
            <button onClick={()=>{onOpenChat(selectedUser);setSelectedUser(null);}} style={{flex:1,padding:'13px',background:'white',color:'#080808',border:'none',borderRadius:'12px',fontWeight:600,fontSize:'0.9rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'7px'}}>
              <MsgIcon/> {conversationIds.has(selectedUser.id)?'Continue':'Message'}
            </button>
            <button onClick={async()=>{
              if(!myId||!selectedUser) return;
              const msg = window.prompt('Send a short message (optional):') || '';
              const exp = new Date(Date.now() + 10*60*1000).toISOString();
              await supabase.from('connection_windows').upsert({sender_id:myId,receiver_id:selectedUser.id,message:msg,expires_at:exp,status:'pending'},{onConflict:'sender_id,receiver_id'});
              setSentWindow({...selectedUser,id:selectedUser.id});
              setSelectedUser(null);
            }} style={{padding:'13px 14px',background:'rgba(255,149,0,0.08)',border:'1px solid rgba(255,149,0,0.25)',borderRadius:'12px',color:'rgba(255,149,0,0.9)',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px',fontSize:'0.78rem',fontWeight:600,whiteSpace:'nowrap'}}>
              ⚡ 10 min
            </button>
          </div>
        </div>
      )}

      {/* Block modal */}
      {showBlockConfirm && targetUser && (
        <BlockModal
          user={targetUser}
          myId={myId}
          onConfirm={(userId) => { blockUser(userId); setShowBlockConfirm(false); setBlockDone(false); }}
          onCancel={() => { setShowBlockConfirm(false); setTargetUser(null); }}
        />
      )}

      {/* Report modal */}
      {showReportModal && targetUser && (
        <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:100,paddingBottom:'24px'}}>
          <div style={{background:'#0f0f0f',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'20px',padding:'24px',width:'calc(100% - 32px)',maxWidth:'400px'}}>
            {!reportDone ? (
              <>
                <h3 style={{color:'white',fontWeight:600,fontSize:'0.95rem',marginBottom:'6px'}}>Report {targetUser.name}</h3>
                <p style={{color:'rgba(255,255,255,0.3)',fontSize:'0.8rem',marginBottom:'14px'}}>Select a reason. Our team will review within 24h.</p>
                <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'12px'}}>
                  {REPORT_REASONS.map(r=>(
                    <button key={r} onClick={()=>setReportReason(r)} style={{padding:'11px 14px',background:reportReason===r?'rgba(255,255,255,0.1)':'rgba(255,255,255,0.03)',border:`1px solid ${reportReason===r?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.06)'}`,borderRadius:'11px',color:reportReason===r?'white':'rgba(255,255,255,0.5)',fontSize:'0.85rem',cursor:'pointer',textAlign:'left'}}>{r}</button>
                  ))}
                </div>
                <textarea value={reportMessage} onChange={e=>setReportMessage(e.target.value)} placeholder="Additional details (optional)" rows={2} style={{width:'100%',padding:'10px 12px',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'10px',color:'white',fontSize:'0.82rem',resize:'none',outline:'none',marginBottom:'12px',boxSizing:'border-box'}}/>
                <div style={{display:'flex',gap:'8px'}}>
                  <button onClick={submitReport} disabled={!reportReason||actionLoading} style={{flex:1,padding:'12px',background:reportReason?'rgba(255,165,0,0.85)':'rgba(255,255,255,0.08)',border:'none',borderRadius:'12px',color:reportReason?'white':'rgba(255,255,255,0.3)',fontWeight:600,fontSize:'0.88rem',cursor:reportReason?'pointer':'not-allowed'}}>
                    {actionLoading?'...':'Submit report'}
                  </button>
                  <button onClick={()=>{setShowReportModal(false);setTargetUser(null);setReportReason('');setReportMessage('');}} style={{flex:1,padding:'12px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'12px',color:'rgba(255,255,255,0.5)',fontSize:'0.88rem',cursor:'pointer'}}>Cancel</button>
                </div>
              </>
            ) : (
              <div style={{textAlign:'center',padding:'8px 0'}}>
                <div style={{width:'44px',height:'44px',background:'rgba(0,214,143,0.1)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10L8 14L16 7" stroke="#00d68f" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <p style={{color:'white',fontWeight:600}}>Report submitted</p>
                <p style={{color:'rgba(255,255,255,0.3)',fontSize:'0.8rem',marginTop:'4px'}}>Our team will review within 24 hours.</p>
              </div>
            )}
          </div>
        </div>
      )}
    {/* Incoming connection window */}
      {activeWindow && (
        <ConnectionWindow
          window={activeWindow}
          session={session}
          onAccept={(cw) => { onOpenChat({id:cw.sender_id,...cw.sender}); setActiveWindow(null); }}
          onDecline={(id) => setActiveWindow(null)}
        />
      )}
    {/* Incoming connection window */}
      {activeWindow && (
        <ConnectionWindow
          window={activeWindow}
          session={session}
          onAccept={(cw) => { onOpenChat({id:cw.sender_id,...cw.sender}); setActiveWindow(null); }}
          onDecline={(id) => setActiveWindow(null)}
        />
      )}
    </div>
  );
}
